'use client'

import { useQueryClient } from '@tanstack/react-query'
import { useTranslations } from 'next-intl'
import { useRef, useState } from 'react'

import { fetchFavoriteChange, useStopBeingFan } from '@/features/clubs/hooks'
import {
  blockFromAvailability,
  blockFromError,
  FAVORITE_CHANGE_KEY,
  type FavoriteChangeBlock
} from '@/features/clubs/lib/favoriteChange'
import { useUserMe } from '@/hooks/useUserMe'
import { showToast } from '@/providers/ToastProvider/ToastMessage'
import Button from '@/shared/ui/Button'
import Dialog from '@/shared/ui/Dialog'
import { HeartIcon } from '@/ui/icons/HeartIcon'

import { FavoriteBlockedDialog } from '../BecomeFanButton/FavoriteDialogs'
import S from './StopBeingFanButton.module.scss'

interface StopBeingFanButtonProps {
  clubId: string
  /** BAT-1180: the same flow on an athlete profile, with athlete wording. */
  isAthlete?: boolean
}

type Step =
  | { kind: 'idle' }
  | { kind: 'confirm' }
  | { kind: 'blocked'; block: FavoriteChangeBlock }

/**
 * «Stop being a fan» clears the favourite — a switch too (BAT-1168): the
 * same rules as «Become a fan» (30 days, tasks in work), checked before the
 * confirmation; a refusal of the save shows the same reason.
 */
const StopBeingFanButton = ({
  clubId,
  isAthlete = false
}: StopBeingFanButtonProps) => {
  const t = useTranslations('club.becomeFan')
  const queryClient = useQueryClient()
  const { data: me } = useUserMe()
  const { mutate, isPending } = useStopBeingFan(clubId)
  const [step, setStep] = useState<Step>({ kind: 'idle' })
  const [isChecking, setIsChecking] = useState(false)
  const busyRef = useRef(false)

  const handleOpen = async () => {
    if (busyRef.current) {
      return
    }
    busyRef.current = true
    setIsChecking(true)
    try {
      const availability = await queryClient.fetchQuery({
        queryKey: [FAVORITE_CHANGE_KEY, me?.user?.id],
        queryFn: fetchFavoriteChange,
        staleTime: 0
      })
      const block = blockFromAvailability(availability)
      setStep(block ? { kind: 'blocked', block } : { kind: 'confirm' })
    } catch {
      // Advisory: the save enforces the rules and a refusal shows the reason.
      setStep({ kind: 'confirm' })
    } finally {
      busyRef.current = false
      setIsChecking(false)
    }
  }

  const handleConfirm = () => {
    mutate(undefined, {
      onSuccess: () => setStep({ kind: 'idle' }),
      onError: async error => {
        const block = await blockFromError(error)
        if (block) {
          setStep({ kind: 'blocked', block })
          return
        }
        showToast({ type: 'error', title: t('error.network') })
      }
    })
  }

  return (
    <>
      <Button
        className={S.trigger}
        color="color"
        type="button"
        onClick={handleOpen}
        disabled={isPending || isChecking}
      >
        {t('stopButton')}
        <HeartIcon className={S.heartIcon} />
      </Button>

      <Dialog
        open={step.kind === 'confirm'}
        toggle={open => {
          if (!open) {
            setStep({ kind: 'idle' })
          }
        }}
        description={
          isAthlete
            ? 'Confirm stopping being a fan of this athlete'
            : 'Confirm stopping being a fan of this club'
        }
      >
        <div className={S.dialogBody}>
          <h3 className={S.dialogTitle}>{t('stopConfirmTitle')}</h3>
          <p className={S.dialogText}>
            {t(isAthlete ? 'stopConfirmTextAthlete' : 'stopConfirmText')}
          </p>
          <div className={S.dialogActions}>
            <Button
              color="secondary"
              type="button"
              onClick={() => setStep({ kind: 'idle' })}
              disabled={isPending}
            >
              {t('cancelButton')}
            </Button>
            <Button
              color="color"
              type="button"
              onClick={handleConfirm}
              disabled={isPending}
            >
              {t('stopConfirmButton')}
            </Button>
          </div>
        </div>
      </Dialog>
      <FavoriteBlockedDialog
        open={step.kind === 'blocked'}
        block={step.kind === 'blocked' ? step.block : null}
        onClose={() => setStep({ kind: 'idle' })}
      />
    </>
  )
}

export default StopBeingFanButton
